import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  BusinessApiError,
  resendInvite,
  type ResendInviteResult,
} from "service/business";

type Props = {
  /** Who the link is for, as the rep knows them. */
  name?: string;
  url: string;
  onDismiss?: () => void;
};

/** Shown when the invite email did not go out. The link is a credential, shown once. */
export function InviteLinkPanel({ name, url, onDismiss }: Readonly<Props>) {
  return (
    <div className="text-sm border rounded-lg p-3 bg-[#FFF1D6]" role="alert">
      <p className="font-semibold">The email could not be delivered.</p>
      <p>Share this link{name ? ` with ${name}` : ""}. It is shown once.</p>
      <p className="break-all select-all mt-1">{url}</p>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="mt-2 text-sm font-semibold text-blue-700 rounded-md px-2 py-1 cursor-pointer hover:bg-[#E1EBFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
        >
          Done
        </button>
      )}
    </div>
  );
}

export type ResentLink = { businessId: string; url: string };

/**
 * Resend an invite. When the email went, a toast says so. When it did not, the
 * link comes back in `link`, held in state only, for the screen to show with
 * InviteLinkPanel.
 */
export function useResendInvite() {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [link, setLink] = useState<ResentLink | null>(null);

  const resend = useCallback(async (businessId: string, email?: string | null) => {
    setBusyId(businessId);
    setLink(null);
    try {
      const res: ResendInviteResult = await resendInvite(businessId);
      if (res.emailSent) {
        toast.success(email ? `Invite resent to ${email}` : "Invite resent.");
      } else {
        setLink({ businessId, url: res.inviteUrl });
      }
      return true;
    } catch (e) {
      toast.error(
        e instanceof BusinessApiError
          ? e.message
          : "Couldn't resend the invite. Try again.",
      );
      return false;
    } finally {
      setBusyId(null);
    }
  }, []);

  return { resend, busyId, link, dismiss: () => setLink(null) };
}
